import { useEffect, useCallback, useRef } from 'react';

/**
 * Hook pour gérer la persistance du formulaire dans sessionStorage
 * et détecter les modifications non sauvegardées
 * 
 * @param {string} storageKey - Clé unique pour le stockage
 * @param {object} formData - Données du formulaire
 * @param {function} setFormData - Setter pour les données du formulaire
 * @param {object} initialData - Données initiales du formulaire
 * @returns {object} { isDirty, clearStorage, loadFromStorage }
 */
export const useFormPersistence = (storageKey, formData, setFormData, initialData) => {
  const initialDataRef = useRef(initialData);
  
  // Vérifier si le formulaire a été modifié
  const isDirty = useCallback(() => {
    if (!formData) return false;
    
    // Compare formData with initial data
    const initial = initialDataRef.current;
    
    // Check if any field has changed from initial values
    for (const key in formData) {
      // Skip comparison for certain fields
      if (key === 'id' || key === 'created_at' || key === 'updated_at') continue;
      
      // Check if value is different from initial
      if (formData[key] !== initial[key]) {
        // Ignore empty string vs undefined/null differences
        if (!((!formData[key] || formData[key] === '') && (!initial[key] || initial[key] === ''))) {
          return true;
        }
      }
    }
    
    return false;
  }, [formData]);
  
  // Sauvegarder dans sessionStorage
  const saveToStorage = useCallback((data, currentStep) => {
    try {
      const storageData = {
        formData: data,
        currentStep: currentStep,
        timestamp: new Date().toISOString()
      };
      sessionStorage.setItem(storageKey, JSON.stringify(storageData));
    } catch (error) {
      console.error('Error saving to sessionStorage:', error);
    }
  }, [storageKey]);
  
  // Charger depuis sessionStorage
  const loadFromStorage = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        // Check if data is not too old (e.g., 1 hour)
        const timestamp = new Date(data.timestamp);
        const now = new Date();
        const hoursDiff = (now - timestamp) / (1000 * 60 * 60);
        
        if (hoursDiff < 1) {
          return data;
        }
      }
    } catch (error) {
      console.error('Error loading from sessionStorage:', error);
    }
    return null;
  }, [storageKey]);
  
  // Effacer le storage
  const clearStorage = useCallback(() => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
    }
  }, [storageKey]);
  
  // Auto-save on formData change (debounced)
  useEffect(() => {
    if (!formData || !isDirty()) return;
    
    const timeoutId = setTimeout(() => {
      // Only save if form is dirty
      if (isDirty()) {
        saveToStorage(formData, 0); // Step will be managed separately
      }
    }, 1000); // 1 second debounce
    
    return () => clearTimeout(timeoutId);
  }, [formData, isDirty, saveToStorage]);
  
  return {
    isDirty: isDirty(),
    clearStorage,
    loadFromStorage,
    saveToStorage
  };
};

/**
 * Hook pour gérer la confirmation avant fermeture
 * 
 * @param {boolean} isDirty - Si le formulaire a des modifications
 * @param {string} message - Message de confirmation personnalisé
 */
export const useConfirmBeforeLeave = (isDirty, message = "Vous avez des modifications non enregistrées. Quitter ?") => {
  useEffect(() => {
    if (!isDirty) return;
    
    // Handle browser navigation/refresh
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, message]);
};
